import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Table,
  Badge,
  Modal,
} from "react-bootstrap";
import { Save, Plus, Edit } from "lucide-react";
import { fetchPeriods, createPeriodSetup } from "./redux/periodSlice";
import { getMasterDataByUserId } from "../../../pages/masterData/redux/masterDataThunk";

import Joyride, { ACTIONS, EVENTS, STATUS } from "react-joyride";
import { useLocation, useNavigate } from "react-router-dom";
import { periodSteps, periodTourSteps } from "../../../tour/timetableTourSteps";
import { handleJoyRideRunning } from "../subjects/redux/subjectSlice";
import { createPortal } from "react-dom";
import { setStepIndex, setStepValid, startTour } from "../../../redux/globalJoyrideSlice";
import GlobalJoyrideCloseButton from "../../../components/GlobalJoyrideCloseButton";

// Utility for duration
const calculateDuration = (start, end) => {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  return eh * 60 + em - (sh * 60 + sm);
};

const formatTimeValue = (value) => (value || "").slice(0, 5);
const getTimeInMinutes = (value) => {
  const safeValue = formatTimeValue(value);
  if (!safeValue) return 0;
  const [hours, minutes] = safeValue.split(":").map(Number);
  return hours * 60 + minutes;
};
const getTimingChangeStorageKey = (schoolId, season) =>
  `periodTimingAck:${schoolId || "unknown"}:${season || "summer"}`;
const getTimingSignature = (seasonData) =>
  [
    formatTimeValue(seasonData?.schoolStart),
    formatTimeValue(seasonData?.schoolEnd),
  ].join("|");

const addMinutesToTime = (time, minutes) => {
  const [h, m] = formatTimeValue(time).split(":").map(Number);
  const total = (((h * 60 + m + minutes) % 1440) + 1440) % 1440;
  const hh = Math.floor(total / 60);
  const mm = total % 60;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
};

const PeriodManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { periods = [], loading, error } = useSelector((s) => s.periods);
  const safePeriods = Array.isArray(periods) ? periods : [];
  const { user } = useSelector((s) => s.authSlice);
  const masterData = useSelector((s) => s.masterData);
  const isUserRole = user?.roles === "user";

  const schoolId = masterData?.school_id;
  const userId = user?.id;
  const adminId = user?.admin_id;

  const [touchedNumber, setTouchedNumber] = useState(false);
  const [touchedDuration, setTouchedDuration] = useState(false);
  const [touchedBreaks, setTouchedBreaks] = useState(false);
  const [previewOpened, setPreviewOpened] = useState(false);
  const [submittedOnce, setSubmittedOnce] = useState(false);

  // FORM STATE
  const [formData, setFormData] = useState({
    schoolStartTime: "08:00",
    schoolEndTime: "12:00",
    assemblyDuration: 30,
    numberOfPeriods: 6,
    periodDuration: 45,
  });

  const [customBreaks, setCustomBreaks] = useState([]);
  const [customTimingEnabled, setCustomTimingEnabled] = useState(false);
  const [customPeriods, setCustomPeriods] = useState([]);
  const [showFormModal, setShowFormModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [season, setSeason] = useState("summer");
  const [showTimingChangedModal, setShowTimingChangedModal] = useState(false);

  // FETCH MASTER DATA
  useEffect(() => {
    if (userId) dispatch(getMasterDataByUserId(userId));
  }, [userId, dispatch]);

  const location = useLocation();
  const globalJoyride = useSelector(state => state.globalJoyride);
  const isPeriodTourRunning =
    globalJoyride.run && globalJoyride.activeTour === "period";

  useEffect(() => {
    console.log("🧠 globalJoyride =", globalJoyride);
  }, [globalJoyride]);


  useEffect(() => {
    if (!location.state?.startPeriodTour) return;

    const waitForDom = setInterval(() => {
      const el = document.querySelector(".create-period-step"); // FIRST STEP TARGET CLASS
      if (el) {
        clearInterval(waitForDom);

        dispatch(
          startTour({
            name: "period",
            steps: periodSteps({
              navigate,
              dispatch,
            }),
          })
        );

        // consume nav state so it doesn't rerun
        navigate(location.pathname, { replace: true, state: {} });

        // force joyride recalculation
        window.dispatchEvent(new Event("resize"));
      }
    }, 100);

    return () => clearInterval(waitForDom);
  }, [location.state, dispatch, navigate, location.pathname]);




  useEffect(() => {
    if (schoolId) dispatch(fetchPeriods(schoolId));
  }, [schoolId, dispatch]);

  const seasonData = masterData?.[season];
  const schoolStartMinutes = getTimeInMinutes(seasonData?.schoolStart);
  const schoolEndMinutes = getTimeInMinutes(seasonData?.schoolEnd);
  const currentTimingSignature = getTimingSignature(seasonData);
  const timingAckStorageKey = getTimingChangeStorageKey(schoolId, season);
  const acknowledgedTimingSignature =
    typeof window !== "undefined"
      ? window.localStorage.getItem(timingAckStorageKey)
      : null;
  const firstSavedStartMinutes = getTimeInMinutes(safePeriods[0]?.start_time);
  const lastSavedEndMinutes = getTimeInMinutes(
    safePeriods[safePeriods.length - 1]?.end_time
  );
  const savedSchoolEndTime = formatTimeValue(
    safePeriods[safePeriods.length - 1]?.end_time
  );
  const masterSchoolEndTime = formatTimeValue(seasonData?.schoolEnd);

  const doesSavedScheduleExceedSchoolTimings =
    !!seasonData &&
    safePeriods.length > 0 &&
    (
      firstSavedStartMinutes < schoolStartMinutes ||
      lastSavedEndMinutes > schoolEndMinutes
    );

  const hasSchoolTimingChanged =
    !!seasonData &&
    safePeriods.length > 0 &&
    masterSchoolEndTime &&
    savedSchoolEndTime &&
    masterSchoolEndTime !== savedSchoolEndTime;
  const shouldShowTimingChangedModal =
    (hasSchoolTimingChanged || doesSavedScheduleExceedSchoolTimings) &&
    acknowledgedTimingSignature !== currentTimingSignature;

  useEffect(() => {
    if (shouldShowTimingChangedModal) {
      setShowTimingChangedModal(true);
    } else {
      setShowTimingChangedModal(false);
    }
  }, [shouldShowTimingChangedModal]);

  // POPULATE FORM WHEN EDITING / CREATING
  useEffect(() => {
    if (!masterData || !season) return;

    const seasonData = masterData[season];
    if (!seasonData) return;

    if (periods?.length > 0) {
      // previously created periods
      const assembly = periods.find((p) => p.period_type === "assembly");
      const breaks = periods.filter((p) => p.period_type === "break");
      const pList = periods.filter((p) => p.period_type === "period");
      const seasonalBreaks = seasonData.breakTimings || [];

      setFormData({
        schoolStartTime: seasonData.schoolStart || periods[0]?.start_time,
        schoolEndTime:
          seasonData.schoolEnd || periods[periods.length - 1]?.end_time,
        assemblyDuration:
          calculateDuration(
            seasonData.assemblyStart,
            seasonData.assemblyEnd
          ) || assembly?.duration || 30,
        numberOfPeriods: pList.length,
        periodDuration: pList[0]?.duration || 45,
      });

      setCustomBreaks(
        (seasonalBreaks.length > 0 ? seasonalBreaks : breaks).map((b, index) => ({
          breakName: b.period_name || `Break ${index + 1}`,
          duration:
            typeof b.duration === "number"
              ? b.duration
              : calculateDuration(b.start, b.end),
          afterPeriod:
            seasonalBreaks.length > 0
              ? index + 1
              : pList.findIndex((p) => p.end_time <= b.start_time) + 1 || null,
          isDeletable: true,
        }))
      );

      setIsEditMode(true);
    } else {
      // fresh create
      setFormData((prev) => ({
        ...prev,
        schoolStartTime: seasonData.schoolStart,
        schoolEndTime: seasonData.schoolEnd,
        assemblyDuration: calculateDuration(
          seasonData.assemblyStart,
          seasonData.assemblyEnd
        ),
      }));

      setCustomBreaks(
        (seasonData.breakTimings || []).map((b, index) => ({
          breakName: `Break ${index + 1}`,
          duration: calculateDuration(b.start, b.end),
          afterPeriod: index + 1,  // FIRST break → after Period 1
          isDeletable: false,

        }))
      );
      console.log("BREAK TIMINGS:", seasonData.breakTimings);


      setIsEditMode(false);
    }
  }, [masterData, periods, season]);

  useEffect(() => {
    if (!showFormModal) return;

    // Scope to this modal only
    const modalBody = document.querySelector(
      ".period-management-modal .modal-body"
    );
    if (!modalBody) return;

    const refresh = () => window.dispatchEvent(new Event("resize"));

    modalBody.addEventListener("scroll", refresh);
    window.addEventListener("scroll", refresh);

    return () => {
      modalBody.removeEventListener("scroll", refresh);
      window.removeEventListener("scroll", refresh);
    };
  }, [showFormModal]);

  // 🔥 JOYRIDE AUTO STEP + SCROLLING LOGIC


  // Generate periods list for preview & save
  const generatePeriodsList = () => {
    const list = [];
    const start = new Date(`2024-01-01T${formData.schoolStartTime}:00`);
    let current = new Date(start);
    let seq = 0;

    // Assembly
    const assemblyEnd = new Date(
      current.getTime() + formData.assemblyDuration * 60000
    );
    list.push({
      period_name: "Assembly",
      start_time: current.toTimeString().slice(0, 5),
      end_time: assemblyEnd.toTimeString().slice(0, 5),
      duration: formData.assemblyDuration,
      sequence_order: seq++,
      period_type: "assembly",
    });

    current = assemblyEnd;

    for (let i = 1; i <= formData.numberOfPeriods; i++) {
      const end = new Date(current.getTime() + formData.periodDuration * 60000);

      list.push({
        period_name: `Period ${i}`,
        start_time: current.toTimeString().slice(0, 5),
        end_time: end.toTimeString().slice(0, 5),
        duration: formData.periodDuration,
        sequence_order: seq++,
        period_type: "period",
      });

      current = end;

      // Insert break if configured
      const breakItem = customBreaks.find((b) => Number(b.afterPeriod) === i);

      if (breakItem) {
        const breakEnd = new Date(
          current.getTime() + breakItem.duration * 60000
        );

        list.push({
          period_name: breakItem.breakName,
          start_time: current.toTimeString().slice(0, 5),
          end_time: breakEnd.toTimeString().slice(0, 5),
          duration: breakItem.duration,
          sequence_order: seq++,
          period_type: "break",
        });

        current = breakEnd;
      }
    }

    return list;
  };

  // Reseed the editable custom-timing table whenever the structure it's
  // built from changes (period count, assembly duration, breaks). This
  // intentionally discards any manual end-time edits made before the change.
  useEffect(() => {
    if (!customTimingEnabled) return;
    setCustomPeriods(generatePeriodsList());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    customTimingEnabled,
    formData.schoolStartTime,
    formData.assemblyDuration,
    formData.numberOfPeriods,
    formData.periodDuration,
    customBreaks,
  ]);

  const handleCustomDurationChange = (index, value) => {
    const duration = parseInt(value, 10);

    if (isNaN(duration) || duration <= 0) {
      alert("Duration must be a positive number of minutes.");
      return;
    }

    setCustomPeriods((prev) => {
      const updated = [...prev];
      const end_time = addMinutesToTime(updated[index].start_time, duration);

      updated[index] = { ...updated[index], duration, end_time };

      for (let i = index + 1; i < updated.length; i++) {
        const prevEnd = updated[i - 1].end_time;
        updated[i] = {
          ...updated[i],
          start_time: prevEnd,
          end_time: addMinutesToTime(prevEnd, updated[i].duration),
        };
      }

      return updated;
    });
  };

  const effectivePeriodsList = customTimingEnabled
    ? customPeriods
    : generatePeriodsList();

  const validatePeriodsWithinSchoolTimings = (periodList) => {
    if (!periodList?.length) return true;

    const generatedStartMinutes = getTimeInMinutes(periodList[0]?.start_time);
    const generatedEndMinutes = getTimeInMinutes(
      periodList[periodList.length - 1]?.end_time
    );
    const allowedStartMinutes = getTimeInMinutes(formData.schoolStartTime);
    const allowedEndMinutes = getTimeInMinutes(formData.schoolEndTime);

    return (
      generatedStartMinutes >= allowedStartMinutes &&
      generatedEndMinutes <= allowedEndMinutes
    );
  };

  const previewPeriods = effectivePeriodsList;
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmittedOnce(true);

    if (isUserRole) return;
    if (!schoolId || !userId) return alert("School or User ID missing.");

    try {
      const periodsToSave = effectivePeriodsList;

      if (!validatePeriodsWithinSchoolTimings(periodsToSave)) {
        alert("Periods timings exceeds the school timings.");
        return;
      }

      await dispatch(
        createPeriodSetup({
          periods: periodsToSave,
          school_id: schoolId,
          user_id: userId,
        })
      ).unwrap();

      if (typeof window !== "undefined") {
        window.localStorage.setItem(
          timingAckStorageKey,
          currentTimingSignature
        );
      }

      alert("Periods saved successfully"); // wait for OK

      setShowFormModal(false);
      setShowTimingChangedModal(false);
      if (isPeriodTourRunning) {
        dispatch(setStepIndex(0)); // reset for next tour stage

        setTimeout(() => {
          navigate("/timetable/generatetimetable", {
            state: { startGenerateTour: true }
          });
        }, 300);
      }


      dispatch(fetchPeriods(schoolId));
    } catch (err) {
      alert("Error saving: " + err);
    }
  };


  return (
    <>
      {/* JOYRIDE */}
      <div id="period-confirm-anchor" />
      {/* <GlobalJoyrideCloseButton /> */}
      {/* MAIN SCREEN */}
      <div className="px-2">
        <Container fluid className="py-3 px-5">
          <Row className="mb-3">
            <Col className="d-flex justify-content-between">
              {!isUserRole && (
                <Button
                  className="btn-sm create-period-step"
                  variant={periods?.length ? "primary" : "success"}
                  onClick={() => {
                    setShowFormModal(true);
                    setTouchedNumber(false);
                    setTouchedDuration(false);
                    setTouchedBreaks(false);
                    setPreviewOpened(false);
                    setSubmittedOnce(false);
                    setCustomTimingEnabled(false);
                    setTimeout(() => {
                      dispatch(setStepIndex(1));
                      window.dispatchEvent(new Event("resize"));
                    }, 350);
                  }}
                >
                  {periods?.length ? (
                    <>
                      <Edit size={16} className="me-1" /> Edit Periods
                    </>
                  ) : (
                    <>
                      <Plus size={16} className="me-1" /> Create Periods
                    </>
                  )}
                </Button>
              )}
            </Col>
          </Row>

          {error && <Alert variant="danger">{error}</Alert>}

          {loading ? (
            <Alert variant="info">Loading periods...</Alert>
          ) : periods?.length > 0 ? (
            <Card>
              <Card.Header
                style={{
                  background:
                    "linear-gradient(to right, #7B2FF7 50%, #F40076 100%)",
                  color: "white",
                }}
              >
                <h5>Current Periods</h5>
              </Card.Header>

              <Card.Body>
                <Table striped hover size="sm">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Start</th>
                      <th>End</th>
                      <th>Duration</th>
                      <th>Type</th>
                    </tr>
                  </thead>

                  <tbody>
                    {periods.map((p, i) => (
                      <tr key={i}>
                        <td>{p.period_name}</td>
                        <td>{p.start_time}</td>
                        <td>{p.end_time}</td>
                        <td>{p.duration} min</td>
                        <td>
                          <Badge
                            bg={
                              p.period_type === "assembly"
                                ? "warning"
                                : p.period_type === "break"
                                  ? "success"
                                  : "primary"
                            }
                          >
                            {p.period_type}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          ) : (
            <Alert variant="info">
              {isUserRole ? "No periods found." : "No periods found. Create one."}
            </Alert>
          )}

          <Modal
            show={showTimingChangedModal}
            onHide={() => setShowTimingChangedModal(false)}
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>School timings changed</Modal.Title>
            </Modal.Header>

            <Modal.Body>
              School timings were changed in master data. You need to edit your
              periods now to match the updated timing.
            </Modal.Body>

            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => setShowTimingChangedModal(false)}
              >
                Close
              </Button>

              {!isUserRole && (
                <Button
                  variant="primary"
                  onClick={() => {
                    setShowTimingChangedModal(false);
                    setShowFormModal(true);
                    setTouchedNumber(false);
                    setTouchedDuration(false);
                    setTouchedBreaks(false);
                    setPreviewOpened(false);
                    setSubmittedOnce(false);
                  }}
                >
                  Edit Periods
                </Button>
              )}
            </Modal.Footer>
          </Modal>

          {/* MODAL */}
          <Modal
            show={showFormModal}
            onHide={() => setShowFormModal(false)}
            size={customTimingEnabled ? "xl" : "lg"}
            centered
            className="period-management-modal"
          >
            <Modal.Header closeButton>
              <Modal.Title>
                {isEditMode ? "Edit Periods" : "Create Periods"}
              </Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <Form onSubmit={handleSubmit}>
                <Row className="">
                  <Col md={customTimingEnabled ? 7 : 12}>
                    <Card className="h-100 ">
                      <Card.Header>School Timings</Card.Header>
                      <Card.Body className="m-2">
                        <Row className="mb-3">
                          <Col>
                            <Form.Group>
                              <Form.Label>Season</Form.Label>
                              <Form.Select disabled value={season}>
                                <option value="summer">Summer</option>
                                <option value="winter">Winter</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>School Start Time</Form.Label>
                              <Form.Control
                                disabled
                                type="time"
                                value={formData.schoolStartTime}
                              />
                            </Form.Group>
                          </Col>

                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>School End Time</Form.Label>
                              <Form.Control
                                disabled
                                type="time"
                                value={formData.schoolEndTime}
                              />
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row className="mt-3">
                          <Col md={4}>
                            <Form.Group>
                              <Form.Label>Assembly (min)</Form.Label>
                              <Form.Control
                                disabled
                                type="number"
                                value={formData.assemblyDuration}
                              />
                            </Form.Group>
                          </Col>

                          <Col md={4}>
                            <Form.Group>
                              <Form.Label>Number of Periods</Form.Label>
                              <Form.Control
                                type="number"
                                className="num-period-step"
                                min={1}
                                max={10}
                                value={formData.numberOfPeriods}
                                onChange={(e) => {
                                  setTouchedNumber(true);

                                  let v = parseInt(e.target.value);
                                  if (isNaN(v) || v < 1) v = 1;
                                  if (v > 10) return alert("Max 10 periods allowed");

                                  setFormData({ ...formData, numberOfPeriods: v });
                                  dispatch(setStepValid(true));
                                }}
                              />
                            </Form.Group>
                          </Col>

                          <Col md={4}>
                            <Form.Group>
                              <Form.Label>Period Duration (min)</Form.Label>
                              <Form.Control
                                className="period-duration-step"
                                type="number"
                                min={1}
                                max={60}
                                value={formData.periodDuration}
                                onChange={(e) => {
                                  setTouchedDuration(true);

                                  const v = parseInt(e.target.value);
                                  if (isNaN(v) || v < 1 || v > 60)
                                    return alert("Duration must be 1–60 min");

                                  setFormData({ ...formData, periodDuration: v });
                                  dispatch(setStepValid(true));
                                }}
                              />
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row className="mt-3">
                          <Col>
                            <Form.Check
                              type="switch"
                              id="custom-timing-switch"
                              label="Enable custom timing for each period"
                              checked={customTimingEnabled}
                              onChange={(e) => setCustomTimingEnabled(e.target.checked)}
                            />
                            {customTimingEnabled && (
                              <Form.Text className="text-muted">
                                Set a duration (min) for each period in the
                                panel on the right — the end time and the
                                next period's start time update automatically.
                                Changing Number of Periods, Assembly, or
                                Breaks resets these custom times.
                              </Form.Text>
                            )}
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>

                  {/* CUSTOM TIMING SIDE PANEL */}
                  {customTimingEnabled && (
                    <Col md={5}>
                      <Card className="h-100">
                        <Card.Header>Custom Period Timings</Card.Header>
                        <Card.Body>
                          <Table striped bordered hover size="sm">
                            <thead >
                              <tr>
                                <th>Name</th>
                                <th>Start</th>
                                <th>End</th>
                                <th>Duration</th>
                              </tr>
                            </thead>
                            <tbody>
                              {customPeriods.map((p, idx) => (
                                <tr key={idx}>
                                  <td>{p.period_name}</td>
                                  <td>{p.start_time}</td>
                                  <td>{p.end_time}</td>
                                  <td>
                                    {p.period_type === "period" ? (
                                      <Form.Control
                                        type="number"
                                        size="sm"
                                        min={1}
                                        max={60}
                                        value={p.duration}
                                        onChange={(e) =>
                                          handleCustomDurationChange(idx, e.target.value)
                                        }
                                      />
                                    ) : (
                                      `${p.duration} min`
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </Card.Body>
                      </Card>
                    </Col>
                  )}
                </Row>

                {/* BREAKS */}
                <Card className="my-4 breaks-step">
                  <Card.Header>Breaks</Card.Header>

                  <Card.Body>
                    {customBreaks.length > 0 && (
                      <Table striped bordered hover size="sm">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Duration</th>
                            <th>After Period</th>
                          </tr>
                        </thead>

                        <tbody>
                          {customBreaks.map((b, i) => (
                            <tr key={i}>
                              <td>{b.breakName}</td>
                              <td>{b.duration} min</td>

                              <td>
                                <Form.Select
                                  value={b.afterPeriod || ""}
                                  onChange={(e) => {
                                    setTouchedBreaks(true);

                                    const val = Number(e.target.value);
                                    const dup = customBreaks.some(
                                      (o, idx) =>
                                        idx !== i && o.afterPeriod === val
                                    );
                                    if (dup)
                                      return alert(
                                        `Break already assigned after Period ${val}`
                                      );

                                    const list = [...customBreaks];
                                    list[i].afterPeriod = val;
                                    setCustomBreaks(list);
                                    dispatch(setStepValid(true));
                                  }}
                                >
                                  <option value="">Select Period</option>
                                  {Array.from(
                                    { length: formData.numberOfPeriods },
                                    (_, j) => (
                                      <option value={j + 1} key={j + 1}>
                                        After Period {j + 1}
                                      </option>
                                    )
                                  )}
                                </Form.Select>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </Card.Body>
                </Card>

                {/* PREVIEW BUTTON */}
                <Button
                  className="preview-step mb-3"
                  variant="outline-secondary"
                  onClick={() => {
                    const next = !showPreview;
                    setShowPreview(next);
                    if (next) setPreviewOpened(true);
                  }}
                >
                  {showPreview ? "Hide Preview" : "Show Preview"}
                </Button>

                {/* PREVIEW TABLE (FULL WIDTH, BOTTOM) */}
                {showPreview && (
                  <Row className="mt-3">
                    <Col>
                      <Card>
                        <Card.Header>Preview</Card.Header>
                        <Card.Body>
                          <Table striped hover size="sm">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Start</th>
                                <th>End</th>
                                <th>Duration</th>
                                <th>Type</th>
                              </tr>
                            </thead>

                            <tbody>
                              {previewPeriods.map((p, idx) => (
                                <tr key={idx}>
                                  <td>{p.period_name}</td>
                                  <td>{p.start_time}</td>
                                  <td>{p.end_time}</td>
                                  <td>{p.duration} min</td>
                                  <td>
                                    <Badge
                                      bg={
                                        p.period_type === "assembly"
                                          ? "warning"
                                          : p.period_type === "break"
                                            ? "success"
                                            : "primary"
                                      }
                                    >
                                      {p.period_type}
                                    </Badge>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                )}

                <Modal.Footer>
                  <Button
                    variant="secondary"
                    onClick={() => setShowFormModal(false)}
                  >
                    Cancel
                  </Button>

                  {!isUserRole && (
                    <Button
                      type="submit"
                      variant="primary"
                      className="submit-period-step"
                    >
                      <Save size={16} className="me-2" />
                      {isEditMode ? "Update periods" : "Create periods"}
                    </Button>
                  )}
                </Modal.Footer>
              </Form>
            </Modal.Body>
          </Modal>
        </Container>
      </div>

      {/* 🔥 SCOPED FIXES FOR JOYRIDE + MODAL SPOTLIGHT (PERIOD MANAGEMENT ONLY) */}
      <style>
        {`
  /* ...existing modal css ... */

  .period-management-modal .modal-body {
    transform: translateZ(0) !important;
    position: relative
  }
`}
      </style>
    </>
  );
};

export default PeriodManagement;
